// src/pages/Clients/clientsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "./clientsAPI";

// ---------- Thunks ----------
export const fetchClients = createAsyncThunk("clients/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await api.fetchClients();
    // normalize dates if needed – keep raw for now
    return res.data;
  } catch (e) {
    return rejectWithValue(e.response?.data || e.message);
  }
});

export const upsertClient = createAsyncThunk("clients/upsert", async (payload, { rejectWithValue }) => {
  try {
    const res = await api.saveClient(payload);
    return res.data;
  } catch (e) {
    return rejectWithValue(e.response?.data || e.message);
  }
});

export const removeClient = createAsyncThunk("clients/remove", async (id, { rejectWithValue }) => {
  try {
    const res = await api.deleteClient({ client_id: id });
    return { id, msg: res.data?.msg };
  } catch (e) {
    return rejectWithValue(e.response?.data || e.message);
  }
});

export const fetchClientUsers = createAsyncThunk("clientUsers/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await api.fetchClientUsers();
    return res.data;
  } catch (e) {
    return rejectWithValue(e.response?.data || e.message);
  }
});

export const upsertClientUser = createAsyncThunk("clientUsers/upsert", async (payload, { rejectWithValue }) => {
  try {
    const res = await api.saveClientUser(payload);
    return res.data;
  } catch (e) {
    return rejectWithValue(e.response?.data || e.message);
  }
});

export const removeClientUser = createAsyncThunk("clientUsers/remove", async (id, { rejectWithValue }) => {
  try {
    const res = await api.deleteClientUser({ client_user_id: id });
    return { id, msg: res.data?.msg };
  } catch (e) {
    return rejectWithValue(e.response?.data || e.message);
  }
});

export const fetchEmailSchedules = createAsyncThunk("emailSchedules/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await api.fetchEmailSchedules();
    return res.data;
  } catch (e) {
    return rejectWithValue(e.response?.data || e.message);
  }
});

export const upsertEmailSchedule = createAsyncThunk("emailSchedules/upsert", async (payload, { rejectWithValue }) => {
  try {
    const res = await api.saveEmailSchedule(payload);
    return res.data;
  } catch (e) {
    return rejectWithValue(e.response?.data || e.message);
  }
});

export const upsertClientPolicy = createAsyncThunk("leavePolicy/upsert", async (payload, { rejectWithValue }) => {
  try {
    const res = await api.saveClientPolicy(payload);
    return res.data;
  } catch (e) {
    return rejectWithValue(e.response?.data || e.message);
  }
});

export const deleteClientPolicy = createAsyncThunk("leavePolicy/delete", async (client_id, { rejectWithValue }) => {
  try {
    const res = await api.deleteClientPolicy({ client_id });
    return { client_id, msg: res.data?.msg };
  } catch (e) {
    return rejectWithValue(e.response?.data || e.message);
  }
});

// ---------- Slice ----------
const slice = createSlice({
  name: "clientsSuite",
  initialState: {
    clients: [],
    clientUsers: [],
    emailSchedules: [],
    loading: { clients: false, users: false, schedules: false, upserting: false },
    error: null,
    clientUserDraft: null,
    shouldReopenClientUserModal: false,
  },
  reducers: {
    saveClientUserDraft: (state, action) => {
  state.clientUserDraft = action.payload;
  state.shouldReopenClientUserModal = true;
},
clearClientUserDraft: (state) => {
  state.clientUserDraft = null;
  state.shouldReopenClientUserModal = false;
},
  },
  extraReducers: (builder) => {
    // Clients
    builder
      .addCase(fetchClients.pending, (s) => { s.loading.clients = true; s.error = null; })
      .addCase(fetchClients.fulfilled, (s, a) => { s.loading.clients = false; s.clients = a.payload || []; })
      .addCase(fetchClients.rejected, (s, a) => { s.loading.clients = false; s.error = a.payload; })

      .addCase(upsertClient.pending, (s) => { s.loading.upserting = true; s.error = null; })
      .addCase(upsertClient.fulfilled, (s) => { s.loading.upserting = false; })
      .addCase(upsertClient.rejected, (s, a) => { s.loading.upserting = false; s.error = a.payload; })

      .addCase(removeClient.fulfilled, (s, a) => {
        s.clients = s.clients.filter((c) => c.id !== a.payload.id);
      });

    // Client Users
    builder
      .addCase(fetchClientUsers.pending, (s) => { s.loading.users = true; s.error = null; })
      .addCase(fetchClientUsers.fulfilled, (s, a) => { s.loading.users = false; s.clientUsers = a.payload || []; })
      .addCase(fetchClientUsers.rejected, (s, a) => { s.loading.users = false; s.error = a.payload; })

      .addCase(upsertClientUser.pending, (s) => { s.loading.upserting = true; s.error = null; })
      .addCase(upsertClientUser.fulfilled, (s) => { s.loading.upserting = false; })
      .addCase(upsertClientUser.rejected, (s, a) => { s.loading.upserting = false; s.error = a.payload; })

      .addCase(removeClientUser.fulfilled, (s, a) => {
        s.clientUsers = s.clientUsers.filter((u) => u.id !== a.payload.id);
      });

    // EmailSchedules
    builder
      .addCase(fetchEmailSchedules.pending, (s) => { s.loading.schedules = true; s.error = null; })
      .addCase(fetchEmailSchedules.fulfilled, (s, a) => { s.loading.schedules = false; s.emailSchedules = a.payload || []; })
      .addCase(fetchEmailSchedules.rejected, (s, a) => { s.loading.schedules = false; s.error = a.payload; })

      .addCase(upsertEmailSchedule.pending, (s) => { s.loading.upserting = true; s.error = null; })
      .addCase(upsertEmailSchedule.fulfilled, (s) => { s.loading.upserting = false; })
      .addCase(upsertEmailSchedule.rejected, (s, a) => { s.loading.upserting = false; s.error = a.payload; });
  },
});

export const { saveClientUserDraft, clearClientUserDraft } = slice.actions;

export default slice.reducer;
